<template>
    <div class="reading-panel">
        <div class="header-section">
            <h4 style="margin: 0; text-align: left;">{{ panel.heading }}</h4>
            <div v-if="panel.headerTooltip" v-tooltip.top="panel.headerTooltip" class="info-icon">
                <i class="pi pi-info-circle"></i>
            </div>
        </div>

        <div v-if="!readingComplete" class="input-section">
            <!-- Numeric entry is the default. A reading whose input isn't a number - the sedation
                 scale - overrides this slot and keeps the rest of the shell, rather than the shell
                 growing a v-if per input shape. -->
            <slot name="input" :panel="panel" :value="value" :setValue="setValue" :submit="submitReading" :canSubmit="hasValue">
                <div style="display: flex; align-items: flex-end; gap: 15px; margin-bottom: 10px;">
                    <div class="small-input" style="flex-grow: 1;">
                        <p style="text-align: left; margin: 0; font-size: 1em; padding-bottom: 8px;">
                            {{ panel.inputLabel }}
                        </p>
                        <InputNumber
                            :minFractionDigits="panel.minFractionDigits"
                            :maxFractionDigits="panel.maxFractionDigits"
                            v-tooltip.top="panel.inputTooltip"
                            mode="decimal"
                            style="border: 1px solid grey; border-radius: 4px; width: 100%;"
                            v-model="value"
                            :placeholder="panel.placeholder"
                        />
                    </div>

                    <button class="blue-button" :disabled="!hasValue" @click="submitReading()">
                        {{ panel.submitLabel }}
                    </button>
                </div>
                <p v-if="panel.helpText" style="font-size: 0.85em; color: #666; text-align: left; margin: 0;">
                    {{ panel.helpText }}
                </p>
            </slot>
        </div>

        <div v-else class="completed-section">
            <div class="success-message">
                <i class="pi pi-check-circle" style="color: #00AD50; font-size: 1.5em; margin-right: 10px;"></i>
                <p style="margin: 0;"><strong>{{ successSummary }}</strong> recorded successfully.</p>
            </div>
        </div>

        <p v-if="errorMessage" style="color: red; text-align: left; margin-top: 10px;">{{ errorMessage }}</p>
    </div>
</template>

<script>
import axios from 'axios';
import { READING_PANELS } from './readingPanels';

export default {
    props: {
        // Key into READING_PANELS - 'bgl' | 'inr' | 'creatinine' | 'sedation'. Matches the values
        // SelectedPatientAdministration's adhocReadingOptions already uses.
        panelType: {
            type: String,
            required: true,
            validator: value => Object.prototype.hasOwnProperty.call(READING_PANELS, value)
        },
        patientId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            value: null,
            readingComplete: false,
            errorMessage: ''
        }
    },
    computed: {
        panel() {
            return READING_PANELS[this.panelType];
        },
        // 0 is a valid reading on every scale here and negative values are valid on the sedation
        // one, so this is an explicit emptiness check rather than a truthiness one. An empty
        // InputNumber yields null; '' guards against a raw input binding.
        hasValue() {
            return this.value !== null && this.value !== '';
        },
        displayValue() {
            return this.panel.formatValue ? this.panel.formatValue(this.value) : this.value;
        },
        successSummary() {
            const parts = [this.panel.successPrefix, this.displayValue, this.panel.successSuffix];
            return parts.filter(part => part !== undefined && part !== null && part !== '').join(' ');
        }
    },
    methods: {
        setValue(value) {
            this.value = value;
        },
        submitReading() {
            if (!this.hasValue) return;

            this.errorMessage = '';

            let payload = {
                patient_id: this.patientId,
                unit: this.panel.unit,
                type: this.panel.type,
                value: this.value
            };

            axios.post('/create-reading', payload, this.$store.state.header)
                .then(res => {
                    this.readingComplete = true;
                    // The consumers unshift this into patient.readings / latest_inr_reading and
                    // sort on created_at, so every field here is load-bearing.
                    this.$emit('readingCompleted', {
                        uuid: res.data.uuid,
                        value: this.value,
                        type: this.panel.type,
                        unit: this.panel.unit,
                        created_at: res.data.created_at
                    });
                })
                .catch(err => {
                    console.error(`Failed to post ${this.panel.type} reading`, err);
                    this.errorMessage = err.response && err.response.data ? err.response.data : this.panel.errorFallback;
                });
        }
    }
}
</script>

<style lang="scss" scoped>
@import "../../assets/css/main.scss";

.reading-panel {
    background-color: #f3f4f4;
    border-radius: 6px;
    padding: 15px;
    margin-bottom: 15px;
}

.header-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 15px;
    border-bottom: 1px solid #ddd;
    padding-bottom: 5px;
}

.info-icon {
    color: #666;
    cursor: help;
}

.small-input {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
}

.success-message {
    display: flex;
    align-items: center;
    background-color: rgba(0, 173, 80, 0.1);
    padding: 10px 15px;
    border-radius: 4px;
    border: 1px solid $complete;
}

.blue-button {
    background-color: $strongblue !important;
    padding: 5px 20px;
    height: 42px; /* Matches standard input height */
    border-radius: 4px;
    cursor: pointer;
    margin: 0;
    color: white;
    border: none;

    &:disabled {
        background-color: #ccc !important;
        cursor: not-allowed;
    }
}
</style>
